import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, BuildingIcon, CalendarIcon } from 'lucide-react';
import { Settlement } from '../types/Transaction';
export function Settlements() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data
  const settlements: Settlement[] = [{
    id: '1',
    settlementId: 'SETTLE-2024-001',
    amount: 145000,
    currency: 'LKR',
    status: 'completed',
    date: '2024-01-15T09:00:00',
    payoutDate: '2024-01-17T14:30:00',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    bankName: 'Genie Business',
    bankAccountNumber: '****1234',
    bankAccountName: 'ABC Store Pvt Ltd',
    branchCode: '001',
    transactionCount: 45,
    transactionIds: ['TXN-2024-001234', 'TXN-2024-001235'],
    fee: 4350,
    netAmount: 140650,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T09:00:00',
      status: 'initiated',
      description: 'Settlement batch created'
    }, {
      id: '2',
      timestamp: '2024-01-16T10:00:00',
      status: 'processing',
      description: 'Processing payout'
    }, {
      id: '3',
      timestamp: '2024-01-17T14:30:00',
      status: 'completed',
      description: 'Funds transferred to merchant account'
    }]
  }, {
    id: '2',
    settlementId: 'SETTLE-2024-002',
    amount: 89500,
    currency: 'LKR',
    status: 'processing',
    date: '2024-01-16T09:00:00',
    merchantName: 'XYZ Restaurant',
    merchantId: 'MERCH-002',
    bankName: 'Commercial Bank',
    bankAccountNumber: '****5678',
    bankAccountName: 'XYZ Restaurant Ltd',
    branchCode: '002',
    transactionCount: 32,
    transactionIds: ['TXN-2024-001236'],
    fee: 2685,
    netAmount: 86815,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-16T09:00:00',
      status: 'initiated',
      description: 'Settlement batch created'
    }, {
      id: '2',
      timestamp: '2024-01-17T10:00:00',
      status: 'processing',
      description: 'Processing payout'
    }]
  }, {
    id: '3',
    settlementId: 'SETTLE-2024-003',
    amount: 234000,
    currency: 'LKR',
    status: 'pending',
    date: '2024-01-17T09:00:00',
    merchantName: 'Tech Solutions Ltd',
    merchantId: 'MERCH-003',
    bankName: 'Sampath Bank',
    bankAccountNumber: '****9012',
    bankAccountName: 'Tech Solutions Ltd',
    branchCode: '003',
    transactionCount: 67,
    transactionIds: ['TXN-2024-001237', 'TXN-2024-001238'],
    fee: 7020,
    netAmount: 226980,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-17T09:00:00',
      status: 'initiated',
      description: 'Settlement batch created'
    }]
  }];
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      reversed: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-5824">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredSettlements = settlements.filter(settlement => {
    const matchesSearch = settlement.settlementId.toLowerCase().includes(searchTerm.toLowerCase()) || settlement.merchantName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || settlement.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalVolume = filteredSettlements.reduce((sum, settlement) => sum + settlement.amount, 0);
  const totalSettlements = filteredSettlements.length;
  const completedSettlements = filteredSettlements.filter(s => s.status === 'completed').length;
  return <div className="space-y-6" data-id="element-5825">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-5826">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5827">
          <div className="text-sm font-medium text-gray-500" data-id="element-5828">Total Volume</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5829">
            Rs. {totalVolume.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5830">
          <div className="text-sm font-medium text-gray-500" data-id="element-5831">
            Total Settlements
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5832">
            {totalSettlements}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5833">
          <div className="text-sm font-medium text-gray-500" data-id="element-5834">Completed</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-5835">
            {completedSettlements}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5836">
          <div className="text-sm font-medium text-gray-500" data-id="element-5837">
            Avg Settlement
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5838">
            Rs.{' '}
            {totalSettlements > 0 ? Math.round(totalVolume / totalSettlements).toLocaleString('en-LK') : 0}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-5839">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-5840">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5841">
            <div className="flex-1 max-w-lg" data-id="element-5842">
              <div className="relative" data-id="element-5843">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-5844" />
                <input type="text" placeholder="Search settlements..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5845" />
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-5846">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5847">
                <option value="all" data-id="element-5848">All Status</option>
                <option value="completed" data-id="element-5849">Completed</option>
                <option value="pending" data-id="element-5850">Pending</option>
                <option value="processing" data-id="element-5851">Processing</option>
                <option value="failed" data-id="element-5852">Failed</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5853">
                <DownloadIcon size={18} data-id="element-5854" />
                Export
              </button>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-5855">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-5856">
            <thead className="bg-gray-50" data-id="element-5857">
              <tr data-id="element-5858">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5859">
                  Settlement ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5860">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5861">
                  Merchant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5862">
                  Transactions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5863">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5864">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5865">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-5866">
              {filteredSettlements.map(settlement => <tr key={settlement.id} className="hover:bg-gray-50" data-id="element-5867">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5868">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5869">
                      {settlement.settlementId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5870">
                    <div className="text-sm text-gray-900" data-id="element-5871">
                      {new Date(settlement.date).toLocaleDateString('en-GB')}
                    </div>
                    {settlement.payoutDate && <div className="text-sm text-gray-500" data-id="element-5872">
                        Paid:{' '}
                        {new Date(settlement.payoutDate).toLocaleDateString('en-GB')}
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5873">
                    <div className="text-sm text-gray-900" data-id="element-5874">
                      {settlement.merchantName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5875">
                      {settlement.merchantId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5876">
                    <div className="text-sm text-gray-900" data-id="element-5877">
                      {settlement.transactionCount} transactions
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5878">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5879">
                      Rs. {settlement.amount.toLocaleString('en-LK')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5880">
                      Net: Rs. {settlement.netAmount.toLocaleString('en-LK')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5881">
                    {getStatusBadge(settlement.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5882">
                    <button onClick={() => navigate(`/settlement-detail/${settlement.id}`)} className="text-blue-600 hover:text-blue-900" title="View Details" data-id="element-5883">
                      <EyeIcon size={18} data-id="element-5884" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSettlements.length === 0 && <div className="text-center py-12" data-id="element-5885">
            <p className="text-gray-500" data-id="element-5886">No settlements found</p>
          </div>}
      </div>
    </div>;
}